// The three user-facing Crafter skills published in the folklore-atelier realm.
// IDs MUST match rows in `<realmDir>/skill-plaza.json`; if they don't the BO
// falls back to a generic broad-analyst skill that produces the wrong output
// structure (Overview / Context & Constraints / Details instead of a story).
export interface StorySkillOption {
  id: string;
  /** Internal slug (kept short for radio/chip data attributes). */
  slug: 'fable' | 'folk-tale' | 'joke';
  /** Display name shown in the picker card. */
  name: string;
  /** Emoji shown on the picker card. */
  emoji: string;
  /** One-line description shown under the name. */
  description: string;
  /** Character budget — surfaced as a soft hint on the textarea. */
  charLimitHint: string;
}

export const STORY_SKILLS: StorySkillOption[] = [
  {
    id: 'fc9c8b7a-6d5e-4f04-8004-000000000004',
    slug: 'fable',
    name: 'Fable',
    emoji: '🦊',
    description:
      'Short animal / personified tale (≤800 chars) ending with a 1–3 sentence moral.',
    charLimitHint: 'Crafts a fable ≤800 chars + a closing moral.',
  },
  {
    id: 'fc9c8b7a-6d5e-4f03-8003-000000000003',
    slug: 'folk-tale',
    name: 'Folk tale',
    emoji: '📜',
    description:
      'Full folk tale (≤1500 chars) with setting, characters and arc — keeps regional imagery and folkloric creatures.',
    charLimitHint: 'Crafts a folk tale ≤1500 chars with a full narrative arc.',
  },
  {
    id: 'fc9c8b7a-6d5e-4f02-8002-000000000002',
    slug: 'joke',
    name: 'Joke',
    emoji: '😄',
    description:
      'Brief regional joke (≤300 chars) with setup + punchline. Enforces CIL/ECL/EIL.',
    charLimitHint: 'Crafts a regional joke ≤300 chars with a punchline.',
  },
];

/** Default skill when the user hasn't picked one yet. */
export const DEFAULT_STORY_SKILL_SLUG: StorySkillOption['slug'] = 'fable';

/** Backwards-compat default skill id (Fable). Some legacy call sites still
 *  reference this; prefer `STORY_SKILLS` + user selection. */
export const FABLE_SKILL_ID: string =
  STORY_SKILLS.find((s) => s.slug === DEFAULT_STORY_SKILL_SLUG)?.id ??
  STORY_SKILLS[0].id;

export const PAGE_SIZE = 20;
export const HOME_FEED_LIMIT = 30;
// Max stories to keep per-space scroll buffer (above + below viewport combined)
export const MAX_CACHE_PER_SPACE = 120;

// NOTE: `DEFAULT_BO_URL` was deleted per
// `docs/requirements/fable-stories-no-user-facing-backend-url-v1.md` §3.5.
// The SPA now always uses **same-origin** (empty base → `buildUrl()` resolves
// against `window.location.href`):
//   - Production:  `https://j31.pub/fable/` → Nginx proxies `^/(v1|auth)/` →
//     server-local `127.0.0.1:<bo_port>` (see ops.py `_aliyun_nginx_server_body`).
//   - Vite dev:    `http://localhost:5193/` → vite proxy (`vite.config.ts`) →
//     `http://127.0.0.1:3010` (override with `BO_URL=` env at dev-server start).
// Backend URL is **system-operations / service-profile** territory (iron rule #8);
// end users MUST NOT see or edit it from any UI surface.

// Map space ID key fragments → display emoji
export const SPACE_EMOJIS: Record<string, string> = {
  africa: '🌍',
  east_asia: '🐉',
  eastern_europe: '🏔️',
  middle_east: '🌙',
  oceania: '🌊',
  south_asia: '🪷',
  western_europe: '🏰',
  cross_cultural: '🌆',
  indigenous: '🦜',
  latin: '🦜',
};

// Short display names for the space chips (extracted from display name before —)
export function spaceShortName(fullName: string): string {
  // "FL · Africa — 非洲民俗"  →  "Africa"
  const m = fullName.match(/FL\s*·\s*(.+?)\s*(?:—|$)/);
  if (m) return m[1].trim();
  return fullName.split('—')[0].replace(/^FL\s*·\s*/i, '').trim() || fullName;
}

export function spaceEmoji(spaceId: string): string {
  const lower = spaceId.toLowerCase();
  for (const [key, emoji] of Object.entries(SPACE_EMOJIS)) {
    if (lower.includes(key)) return emoji;
  }
  return '📚';
}
